/**
 * Typed API error taxonomy. The fetch core throws the most specific subclass for
 * a non-2xx response (or `success:false` envelope) so the UI can branch on type
 * instead of sniffing status codes:
 *
 *   401 → UnauthorizedError  (handled by the core: refresh→retry→onUnauthorized)
 *   403 → ForbiddenError     ("Nemáte oprávnění" surface)
 *   404 → NotFoundError
 *   409 → ConflictError      (optimistic-lock — reload + re-apply; carries currentVersion)
 *   410 → GoneError          (expired/used token, e.g. reset link)
 *   422 → ValidationError    (field errors → react-hook-form setError)
 */

export interface ApiErrorOptions {
  status: number;
  code?: string;
  details?: unknown;
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details?: unknown;

  constructor(message: string, opts: ApiErrorOptions) {
    super(message);
    this.name = 'ApiError';
    this.status = opts.status;
    this.code = opts.code ?? `http_${opts.status}`;
    this.details = opts.details;
  }
}

export class UnauthorizedError extends ApiError {
  constructor(message = 'Unauthorized', opts: Omit<ApiErrorOptions, 'status'> = {}) {
    super(message, { ...opts, status: 401 });
    this.name = 'UnauthorizedError';
  }
}

export class ForbiddenError extends ApiError {
  constructor(message = 'Forbidden', opts: Omit<ApiErrorOptions, 'status'> = {}) {
    super(message, { ...opts, status: 403 });
    this.name = 'ForbiddenError';
  }
}

export class NotFoundError extends ApiError {
  constructor(message = 'Not found', opts: Omit<ApiErrorOptions, 'status'> = {}) {
    super(message, { ...opts, status: 404 });
    this.name = 'NotFoundError';
  }
}

/**
 * 409 — optimistic-lock conflict (`@VersionColumn` mismatch). The edit form
 * should reload the entity and ask the user to re-apply. `currentVersion` is
 * surfaced when the API includes it in `error.details`.
 */
export class ConflictError extends ApiError {
  readonly currentVersion?: number;
  constructor(message = 'Conflict', opts: Omit<ApiErrorOptions, 'status'> = {}) {
    super(message, { ...opts, status: 409 });
    this.name = 'ConflictError';
    const d = opts.details as { currentVersion?: number } | undefined;
    this.currentVersion = typeof d?.currentVersion === 'number' ? d.currentVersion : undefined;
  }
}

export class GoneError extends ApiError {
  constructor(message = 'Gone', opts: Omit<ApiErrorOptions, 'status'> = {}) {
    super(message, { ...opts, status: 410 });
    this.name = 'GoneError';
  }
}

/** A single field-level validation failure. */
export interface FieldError {
  field: string;
  message: string;
}

/**
 * 422 — validation. `fieldErrors` is best-effort extracted from common shapes
 * (`details.fieldErrors`, `details.errors`, class-validator arrays) so a form
 * can map them onto fields; empty when the API only sent a message.
 */
export class ValidationError extends ApiError {
  readonly fieldErrors: FieldError[];
  constructor(message = 'Validation failed', opts: Omit<ApiErrorOptions, 'status'> = {}) {
    super(message, { ...opts, status: 422 });
    this.name = 'ValidationError';
    this.fieldErrors = extractFieldErrors(opts.details);
  }
}

function extractFieldErrors(details: unknown): FieldError[] {
  if (!details || typeof details !== 'object') return [];
  const d = details as Record<string, unknown>;
  const raw = (d.fieldErrors ?? d.errors ?? d.violations) as unknown;
  if (!Array.isArray(raw)) return [];
  const out: FieldError[] = [];
  for (const item of raw) {
    if (item && typeof item === 'object') {
      const o = item as Record<string, unknown>;
      const field = (o.field ?? o.property ?? o.path) as unknown;
      const message = (o.message ?? o.constraint ?? o.error) as unknown;
      if (typeof field === 'string' && typeof message === 'string') {
        out.push({ field, message });
      }
    }
  }
  return out;
}

/** Build the most specific error subclass for a status + envelope error. */
export function errorFromResponse(
  status: number,
  message: string,
  code?: string,
  details?: unknown,
): ApiError {
  const opts = { code, details };
  switch (status) {
    case 401:
      return new UnauthorizedError(message, opts);
    case 403:
      return new ForbiddenError(message, opts);
    case 404:
      return new NotFoundError(message, opts);
    case 409:
      return new ConflictError(message, opts);
    case 410:
      return new GoneError(message, opts);
    case 422:
      return new ValidationError(message, opts);
    default:
      return new ApiError(message, { status, code, details });
  }
}
